import { DateTime } from '@ksyos/date-time';
import { Button as DesignSystemButton, Modal, Text } from '@ksyos/design-system';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { connect } from 'react-redux';
import type { IRequest } from '../../../common/interfaces/IRequest';
import type { IDataRequest } from '../../../common/utils/IDataRequest';
import type { IReduxState } from '../../configureStore';
import { ContentType } from '../../../common/enums/ContentType';
import { DataRequestStatus } from '../../../common/enums/DataRequestStatus';
import { RequestStatus } from '../../../common/enums/RequestStatus';
import { ServiceCode } from '../../../common/enums/ServiceCode';
import { fetch, fetchJson } from '../../store/fetch';
import { translateKey } from '../../translate';
import { Button } from '../elements/atoms/buttons/button/Button';
import { PageTitle } from '../elements/atoms/pageTitle/PageTitle';
import { Paragraph } from '../elements/atoms/paragraph/Paragraph';
import { Component } from '../elements/Component';
import { Alert } from '../elements/molecules/alert/Alert';
import { Article } from '../elements/molecules/article/article';
import { Card } from '../elements/molecules/card/Card';
import { Container } from '../elements/molecules/container/Container';
import { Content } from '../elements/molecules/content/Content';
import { Main } from '../elements/molecules/main/Main';
import { PageSection } from '../elements/molecules/pageSection/PageSection';
import { Footer } from '../elements/organisms/footer/Footer';
import { Header } from '../elements/organisms/header/Header';
import { PageWrapper } from '../elements/organisms/pageWrapper/PageWrapper';
import { withSearchParams } from '../hoc/withSearchParams';

interface IYourDataProps {
    searchParams: URLSearchParams;
}

interface IYourDataReduxProps {
    isOsasConsultationMissing: boolean;
}

interface IYourDataState {
    isFlexOsasConsultationMissing?: boolean;
    isLoading?: boolean;
    isMedicalDataAvailable?: boolean;
    isNsiPatient?: boolean;
    isOsasConsultationMissingModalOpen: boolean;
    pdfRequest?: IDataRequest;
    xmlRequest?: IDataRequest;
}

const mapStateToProps = ({ authentication }: IReduxState): IYourDataReduxProps => ({
    isOsasConsultationMissing: getIsOsasConsultationMissing(authentication.requestProcesses),
});

class YourDataWrapper extends Component<IYourDataProps & IYourDataReduxProps, IYourDataState> {
    public async componentDidMount() {
        await this.setState({ isLoading: true });
        await this.getDataRequests();
        await this.setState({ isLoading: false });
        await this.checkRequestError();
        await this.getIsMedicalDataAvailable();
        await this.loadIsOsasConsultationMissing();
    }

    public render() {
        const pdfCardText = 'Dit bestand bevat jouw gegevens in tekst en afbeeldingen';
        const xmlCardText = this.state.isMedicalDataAvailable
            ? 'Dit bestand bevat jouw gegevens in XML-code. (Gebruik dit bestand in je Persoonlijke Gezondheidsomgeving of als je informatie uitwisselt met andere zorgverleners)'
            : '';

        const nsiText = `Je dossier met betrekking tot het slaaponderzoek
        kun je opvragen bij het Nederlands Slaap Instituut na het
        videoconsult met de medisch specialist (somnoloog).`;

        return (
            <PageWrapper fullHeight>
                <Header
                    backButtonText={translateKey('common.home')}
                    backUrl="/"
                    hasBackbutton
                    hasLogoutButton
                />
                <Main>
                    <Container isFluidOnMobile>
                        <PageSection>
                            <Article>
                                <PageTitle>Vraag je gegevens op</PageTitle>
                            </Article>
                        </PageSection>
                        <PageSection>
                            <Content>
                                <Row>
                                    <Col
                                        className="mb-3"
                                        lg={12}
                                        xs={12}
                                    >
                                        <div style={{ backgroundColor: '#fff', margin: 0, padding: '24px' }}>
                                            Vraag hier je medische gegevens op die bekend zijn bij Ksyos. Download alle
                                            informatie in een PDF of XML-bestand.
                                            {this.state.isNsiPatient && nsiText}
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col
                                        className="mb-3"
                                        lg={6}
                                        style={{ display: 'flex' }}
                                        xs={12}
                                    >
                                        {!this.state.isLoading && (
                                            <Card
                                                body={pdfCardText}
                                                cardTitle="PDF-bestand"
                                                headerLarge
                                                style={{
                                                    display: 'flex',
                                                    flexBasis: '100%',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                {this.state.pdfRequest &&
                                                    this.state.pdfRequest.status === DataRequestStatus.downloadFailed &&
                                                    this.renderErrorMessage('download')}

                                                {this.state.pdfRequest &&
                                                    this.state.pdfRequest.status === DataRequestStatus.failed &&
                                                    this.renderErrorMessage('request')}

                                                <div style={{ marginTop: 'auto' }}>
                                                    <div style={{ marginTop: '35px' }}>
                                                        <Button
                                                            isLoading={
                                                                this.state.pdfRequest &&
                                                                this.state.pdfRequest.status ===
                                                                    DataRequestStatus.inProgress
                                                            }
                                                            onClick={async () => {
                                                                if (
                                                                    this.props.isOsasConsultationMissing ||
                                                                    this.state.isFlexOsasConsultationMissing
                                                                ) {
                                                                    await this.setState({
                                                                        isOsasConsultationMissingModalOpen: true,
                                                                    });
                                                                } else {
                                                                    await this.onRequestPdf();
                                                                }
                                                            }}
                                                        >
                                                            Download gegevens
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Card>
                                        )}
                                    </Col>
                                    <Col
                                        className="mb-3"
                                        lg={6}
                                        xs={12}
                                    >
                                        {!this.state.isLoading && (
                                            <Card
                                                body={xmlCardText}
                                                cardTitle="XML-bestand"
                                                headerLarge
                                                style={{
                                                    display: 'flex',
                                                    flexBasis: '100%',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                {this.state.xmlRequest &&
                                                    this.state.xmlRequest.status === DataRequestStatus.downloadFailed &&
                                                    this.renderErrorMessage('download')}

                                                {this.state.xmlRequest &&
                                                    this.state.xmlRequest.status === DataRequestStatus.failed &&
                                                    this.renderErrorMessage('request')}

                                                {!this.state.isMedicalDataAvailable && (
                                                    <Alert
                                                        alertHeading="De aanvraag is voltooid"
                                                        variant="success"
                                                    >
                                                        <Paragraph>
                                                            Er zijn tot op heden geen medische gegevens van jou bekend
                                                            bij Ksyos.
                                                        </Paragraph>
                                                    </Alert>
                                                )}

                                                {this.state.isMedicalDataAvailable && (
                                                    <div style={{ marginTop: 'auto' }}>
                                                        <div style={{ marginTop: '35px' }}>
                                                            <Button
                                                                isLoading={
                                                                    this.state.xmlRequest &&
                                                                    this.state.xmlRequest.status ===
                                                                        DataRequestStatus.inProgress
                                                                }
                                                                onClick={() => this.onRequestXml()}
                                                            >
                                                                Download gegevens
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </Card>
                                        )}
                                    </Col>
                                </Row>
                            </Content>
                        </PageSection>
                    </Container>
                </Main>
                <Footer />

                {this.state.isOsasConsultationMissingModalOpen && (
                    <Modal
                        isOpen={this.state.isOsasConsultationMissingModalOpen}
                        onClose={async () => await this.setState({ isOsasConsultationMissingModalOpen: false })}
                    >
                        <Modal.Header>Je dossier is nog niet compleet</Modal.Header>
                        <Modal.Body>
                            <Text>
                                De resultaten van jouw slaaponderzoek zijn nog niet beoordeeld door de specialist. Na
                                het consult wordt die belangrijke informatie in je dossier toegevoegd. Weet je zeker dat
                                je het dossier wilt downloaden?
                            </Text>
                        </Modal.Body>
                        <Modal.Footer>
                            <DesignSystemButton
                                onClick={async () => {
                                    await this.setState({ isOsasConsultationMissingModalOpen: false });
                                    await this.onRequestPdf();
                                }}
                                variant="outline"
                            >
                                Ja
                            </DesignSystemButton>
                            <DesignSystemButton
                                onClick={async () => await this.setState({ isOsasConsultationMissingModalOpen: false })}
                            >
                                Annuleer
                            </DesignSystemButton>
                        </Modal.Footer>
                    </Modal>
                )}
            </PageWrapper>
        );
    }

    private renderErrorMessage(errorMessageType: 'request' | 'download') {
        const heading: string =
            errorMessageType === 'request' ? 'De aanvraag is niet gelukt' : 'Het downloaden is niet gelukt';

        return (
            <Alert
                alertHeading={heading}
                variant="warning"
            >
                <Paragraph>
                    Er is iets mis gegaan. Neem voor hulp contact op tijdens kantooruren (08:30 - 17:00) met de Ksyos
                    Servicedesk (020-600 00 60).
                </Paragraph>
            </Alert>
        );
    }

    private async onRequestPdf() {
        await this.setState({
            pdfRequest: { format: ContentType.applicationPdf, status: DataRequestStatus.inProgress },
        });
        await this.sendDataRequest(ContentType.applicationPdf);
        await this.getDataRequests();

        if (this.state.pdfRequest?.id && this.state.pdfRequest.status === DataRequestStatus.processed) {
            await this.onDownloadDataRequest(this.state.pdfRequest?.id, ContentType.applicationPdf);
        }
    }

    private async onRequestXml() {
        await this.setState({
            xmlRequest: { format: ContentType.applicationFhirXml, status: DataRequestStatus.inProgress },
        });
        await this.sendDataRequest(ContentType.applicationFhirXml);
        await this.getDataRequests();

        if (this.state.xmlRequest?.id && this.state.xmlRequest.status === DataRequestStatus.processed) {
            await this.onDownloadDataRequest(this.state.xmlRequest?.id, ContentType.applicationFhirXml);
        }
    }

    private async sendDataRequest(format: ContentType.applicationPdf | ContentType.applicationFhirXml) {
        let result;
        try {
            result = await fetchJson('/api/dataRequests', 'POST', { format });
        } catch {
            result = undefined;
        }

        if (!result || !result.json || !result.json.id) {
            if (format === ContentType.applicationPdf) {
                await this.setState({
                    pdfRequest: {
                        format,
                        status: DataRequestStatus.failed,
                    },
                });
            } else {
                await this.setState({
                    xmlRequest: {
                        format,
                        status: DataRequestStatus.failed,
                    },
                });
            }
            throw new Error('Error sending data request ' + format);
        }
    }

    private async getDataRequests() {
        const result = await fetchJson('/api/dataRequests', 'GET');

        if (!result.json || !result.json.items) {
            throw new Error('Error getting data requests');
        }
        const dataRequests: IDataRequest[] = result.json.items;
        const oneWeekAgo = DateTime.now().subtract(7, 'days');

        let newPdfRequest: IDataRequest | undefined;
        let newXmlRequest: IDataRequest | undefined;

        const lastPdfRequest = dataRequests.find(
            (request: IDataRequest) => request.format === ContentType.applicationPdf
        );
        if (lastPdfRequest) {
            if (
                (lastPdfRequest.status === DataRequestStatus.processed &&
                    lastPdfRequest.fileProcessedAt &&
                    DateTime.from(lastPdfRequest.fileProcessedAt).isAfter(oneWeekAgo)) ||
                lastPdfRequest.status === DataRequestStatus.open
            ) {
                newPdfRequest = lastPdfRequest;
            }
        }

        const lastXmlRequest = dataRequests.find(
            (request: IDataRequest) => request.format === ContentType.applicationFhirXml
        );
        if (lastXmlRequest) {
            if (
                (lastXmlRequest.status === DataRequestStatus.processed &&
                    lastXmlRequest.fileProcessedAt &&
                    DateTime.from(lastXmlRequest.fileProcessedAt).isAfter(oneWeekAgo)) ||
                lastXmlRequest.status === DataRequestStatus.open
            ) {
                newXmlRequest = lastXmlRequest;
            }
        }

        await this.setState({
            isNsiPatient: result.json.isNsiPatient,
            pdfRequest: newPdfRequest,
            xmlRequest: newXmlRequest,
        });
    }

    private async onDownloadDataRequest(dataRequestId: string | undefined, contentType: ContentType) {
        if (dataRequestId) {
            const res = await fetch(
                `/api/dataRequests/${dataRequestId}?format=${encodeURIComponent(contentType)}`,
                'GET'
            );
            if (res.status !== 200) {
                if (contentType === ContentType.applicationPdf) {
                    await this.setState(({ pdfRequest }) => ({
                        pdfRequest: {
                            ...(pdfRequest as IDataRequest),
                            status: DataRequestStatus.downloadFailed,
                        },
                    }));
                } else {
                    await this.setState(({ xmlRequest }) => ({
                        xmlRequest: {
                            ...(xmlRequest as IDataRequest),
                            status: DataRequestStatus.downloadFailed,
                        },
                    }));
                }
            } else {
                window.location.href = `/api/dataRequests/${dataRequestId}?format=${encodeURIComponent(contentType)}`;
            }
        }
    }

    private async checkRequestError() {
        const searchParams = this.props.searchParams;
        const requestError = searchParams.get('error') as DataRequestStatus | null;
        if (requestError) {
            await this.setState({
                pdfRequest: {
                    format: ContentType.applicationPdf,
                    status: requestError,
                },
                xmlRequest: {
                    format: ContentType.applicationFhirXml,
                    status: requestError,
                },
            });
        }
    }

    private async getIsMedicalDataAvailable() {
        const result = await fetchJson('/api/patient/medicalDataAvailable', 'GET');

        if (!result.json) {
            throw new Error('Error getting is medical data available');
        }

        this.setState({ isMedicalDataAvailable: result.json.isMedicalDataAvailable });
    }

    private async loadIsOsasConsultationMissing() {
        const result = await fetchJson(`/api/patient/isOsasConsultationMissing`, 'GET');

        if (result.json) {
            this.setState({ isFlexOsasConsultationMissing: result.json.isOsasConsultationMissing });
        }
    }
}

function getIsOsasConsultationMissing(requests?: IRequest[]) {
    const lastOsasExaminationRequest = requests?.find((request: IRequest) =>
        [ServiceCode.OSASO, ServiceCode.OSASVO].includes(request.service.code as ServiceCode)
    );
    const lastCompletedOsasConsultationRequest = requests?.find(
        (request: IRequest) =>
            [ServiceCode.OSASC2, ServiceCode.OSASVC].includes(request.service.code as ServiceCode) &&
            request.status === RequestStatus.completed
    );

    return (
        (!!lastOsasExaminationRequest && !lastCompletedOsasConsultationRequest) ||
        (!!lastOsasExaminationRequest?.dateSent &&
            !!lastCompletedOsasConsultationRequest?.dateSent &&
            DateTime.from(lastOsasExaminationRequest.dateSent).isAfter(lastCompletedOsasConsultationRequest.dateSent))
    );
}

export const YourData = connect(mapStateToProps)(withSearchParams(YourDataWrapper));
